import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { base64ImageEncode, downloadFile, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

export const fetchDocuments = createAsyncThunk(
    "document/fetchDocuments",
    async (beneficiarId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiarId}/documents`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchDocument = createAsyncThunk(
    "document/fetchDocument",
    async ({ file, beneficiaryId }: { file: any; beneficiaryId: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(
                    import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/documents/${file.id}`,
                ),
                {
                    pending: `${t("AddDocument.DownloadDocumentPending")}`,
                    success: `${t("AddDocument.DownloadDocumentSuccess")}`,
                    error: `${t("AddDocument.DownloadDocumentError")}`,
                },
            );
            const linkSource = `data:${file.mimetype ?? "text/plain"};base64,${response.data}`;
            const fileName = file.name;
            downloadFile({ source: linkSource, fileName, isLink: true });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const sendDocuments = createAsyncThunk(
    "document/sendDocuments",
    async ({ formData, beneficiarId }: { formData: FormData; beneficiarId: number }, { rejectWithValue }) => {
        try {
            const config = {
                headers: {
                    "content-type": "multipart/form-data",
                },
            };
            const response = await toast.promise(
                axios.post(
                    import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiarId}/documents`,
                    formData,
                    config,
                ),
                {
                    pending: `${t("AddDocument.SendDocumentPending")}`,
                    success: `${t("AddDocument.SendDocumentSuccess")}`,
                    error: `${t("AddDocument.SendDocumentError")}`,
                },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteDocument = createAsyncThunk(
    "document/deleteDocument",
    async ({ beneficiarId, documentId }: { beneficiarId: number; documentId: number }, { rejectWithValue }) => {
        try {
            await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiarId}/documents/${documentId}`,
            );
            return documentId;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/** Qualify (type + validity), reject (status + reason) or rename a beneficiary document. */
export const updateDocument = createAsyncThunk(
    "document/updateDocument",
    async (
        { beneficiarId, documentId, data }: { beneficiarId: number; documentId: number; data: TUpdateDocumentPayload },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.patch(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiarId}/documents/${documentId}`,
                data,
            );
            return response.data.data as IDocument;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchDocumentsBP = createAsyncThunk(
    "document/fetchDocumentsBP",
    async ({ companyId }: { companyId?: string }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + "/documents",
                companyId ? { params: { companyId } } : {},
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/** One document type the author's publisher expects from them, and where it stands */
export interface IExpectedDocument {
    typeId: number;
    typeName: string;
    requiresValidity: boolean;
    status: "missing" | "pending" | "valid" | "expiring_soon" | "expired";
    validFrom: string | null;
    validUntil: string | null;
    documentId: number | null;
    /** An upload of this type awaits the publisher's qualification (also while the current one is valid) */
    hasPending: boolean;
    /** Reason of the latest rejected upload of this type, while nothing replaced it */
    rejectionReason: string | null;
}

export interface IExpectedDocumentsByCompany {
    companyId: number;
    expected: IExpectedDocument[];
}

export const fetchExpectedDocumentsBP = createAsyncThunk(
    "document/fetchExpectedDocumentsBP",
    async (_: void, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + "/documents/expected",
            );
            return response.data.data as IExpectedDocumentsByCompany[];
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const fetchDocumentBP = createAsyncThunk("document/fetchDocumentBP", async (file: any, { rejectWithValue }) => {
    try {
        const response = await toast.promise(
            axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/documents/${file.id}`),
            {
                pending: `${t("AddDocument.DownloadDocumentPending")}`,
                success: `${t("AddDocument.DownloadDocumentSuccess")}`,
                error: `${t("AddDocument.DownloadDocumentError")}`,
            },
        );
        const linkSource = `data:${file.mimetype ?? "text/plain"};base64,${response.data}`;
        const fileName = file.name;
        downloadFile({ source: linkSource, fileName, isLink: true });
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const sendDocumentsBP = createAsyncThunk(
    "document/sendDocumentsBP",
    async ({ formData, companyId }: { formData: FormData; companyId: string | string[] }, { rejectWithValue }) => {
        try {
            const config = {
                headers: {
                    "content-type": "multipart/form-data",
                },
                params: typeof companyId === "string" ? { companyId } : { companyId: companyId.join() },
            };
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + "/documents", formData, config),
                {
                    pending: `${t("AddDocument.SendDocumentPending")}`,
                    success: `${t("AddDocument.SendDocumentSuccess")}`,
                    error: `${t("AddDocument.SendDocumentError")}`,
                },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteDocumentBP = createAsyncThunk(
    "document/deleteDocumentBP",
    async ({ documentId }: { documentId: number }, { rejectWithValue }) => {
        try {
            await axios.delete(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/documents/${documentId}`);
            return documentId;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Upload file to get link (as in metadata):
export const uploadFile = createAsyncThunk(
    "documents/uploadFile",
    async ({ formData }: { formData: FormData }, { rejectWithValue }) => {
        try {
            const config = {
                headers: {
                    "content-type": "multipart/form-data",
                },
            };
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/images", formData, config),
                {
                    pending: `${t("AddDocument.DocumentUploadPending")}`,
                    success: `${t("AddDocument.DocumentUploadSuccess")}`,
                    error: `${t("AddDocument.DocumentUploadError")}`,
                },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const uploadPicture = createAsyncThunk("documents/uploadPicture", async (file: File, { rejectWithValue }) => {
    try {
        const base64string = await base64ImageEncode(file);
        const payload = {
            name: file.name,
            mime: file.type,
            size: file.size,
            content: base64string,
        };
        const response = await axios.post<{ imagePath: string }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/images/upload-base64",
            payload,
        );
        return response.data.imagePath;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export interface IDocument {
    addedAt: Date;
    beneficiarId: number;
    companyId: number;
    id: number;
    mimetype: string;
    name: string;
    size: string;
    storageId: string;
    uploadedBy: string;
    isArchived?: number | boolean;
    /** null = untyped ("other") document */
    documentTypeId?: number | null;
    validFrom?: string | null;
    validUntil?: string | null;
    /** pending = uploaded by the author, to be qualified or rejected by the publisher */
    status?: "pending" | "valid" | "rejected";
    rejectionReason?: string | null;
    validatedBy?: string | null;
    validatedAt?: string | null;
}

export type TUpdateDocumentPayload = {
    name?: string;
    isArchived?: boolean;
    documentTypeId?: number | null;
    validFrom?: string | null;
    validUntil?: string | null;
    status?: "valid" | "rejected";
    rejectionReason?: string;
};

export interface DocumentState {
    document: string;
    documentList: any[];
    documentListLoadStatus: TFetchStatus;
    pictureStatus: TFetchStatus;
    /** Author portal: expected documents per company */
    expectedDocuments: IExpectedDocumentsByCompany[];
    expectedDocumentsStatus: TFetchStatus;
}

const initialState: DocumentState = {
    document: "",
    documentList: [],
    documentListLoadStatus: "idle",
    pictureStatus: "idle",
    expectedDocuments: [],
    expectedDocumentsStatus: "idle",
};

export const documentSlice = createSlice({
    name: "document",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(fetchDocumentsBP.fulfilled, (state, action) => {
            state.documentListLoadStatus = "succeeded";
            state.documentList = action.payload;
        });
        builder.addCase(fetchDocumentsBP.pending, (state, action) => {
            state.documentListLoadStatus = "pending";
        });
        builder.addCase(fetchDocumentsBP.rejected, (state, { payload }) => {
            state.documentListLoadStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchDocumentBP.fulfilled, (state, action) => {
            state.document = action.payload;
        });
        builder.addCase(fetchExpectedDocumentsBP.pending, (state) => {
            state.expectedDocumentsStatus = "pending";
        });
        builder.addCase(fetchExpectedDocumentsBP.fulfilled, (state, action) => {
            state.expectedDocumentsStatus = "succeeded";
            state.expectedDocuments = action.payload;
        });
        builder.addCase(fetchExpectedDocumentsBP.rejected, (state) => {
            state.expectedDocumentsStatus = "failed";
        });

        builder.addCase(sendDocumentsBP.pending, () => {});
        builder.addCase(sendDocumentsBP.fulfilled, (state, action) => {
            if (action.payload) state.documentList.unshift(action.payload);
        });
        builder.addCase(sendDocumentsBP.rejected, () => {});

        builder.addCase(fetchDocuments.fulfilled, (state, action) => {
            state.documentListLoadStatus = "succeeded";
            state.documentList = action.payload;
        });
        builder.addCase(fetchDocuments.pending, (state, action) => {
            state.documentListLoadStatus = "pending";
            state.documentList = [];
        });
        builder.addCase(fetchDocuments.rejected, (state, { payload }) => {
            state.documentListLoadStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchDocument.fulfilled, (state, action) => {
            state.document = action.payload;
        });

        builder.addCase(sendDocuments.pending, () => {});
        builder.addCase(sendDocuments.fulfilled, (state, action) => {
            if (action.payload) state.documentList.push(action.payload);
        });
        builder.addCase(sendDocuments.rejected, () => {});

        builder.addCase(updateDocument.fulfilled, (state, action) => {
            state.documentList = state.documentList.map((document: IDocument) =>
                document.id === action.payload.id ? action.payload : document,
            );
            toast.success(`${t("BeneficiaryDocuments.Documents.UpdateSuccess")}`);
        });
        builder.addCase(updateDocument.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(deleteDocument.fulfilled, (state, action) => {
            state.documentList = state.documentList.filter((document: IDocument) => document.id !== action.payload);
            toast.success(`${t("Terms.PDFDeletedSuccessfully")}`);
        });
        builder.addCase(deleteDocument.rejected, (action, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(deleteDocumentBP.fulfilled, (state, action) => {
            state.documentList = state.documentList.filter((document: IDocument) => document.id !== action.payload);
            toast.success(`${t("Terms.PDFDeletedSuccessfully")}`);
        });
        builder.addCase(deleteDocumentBP.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(uploadPicture.fulfilled, (state, { payload }) => {
            toast.success(`${t("Beneficiars.AddPictureSuccess")}`);
            state.pictureStatus = "succeeded";
        });
        builder.addCase(uploadPicture.pending, (state) => {
            state.pictureStatus = "pending";
        });
        builder.addCase(uploadPicture.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.pictureStatus = "failed";
        });
    },
});

export default documentSlice.reducer;
